import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import { Stack, Tooltip } from '@mui/material';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import axiosInstance from 'src/utils/axios';
import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deletePackagingItem,
  getPackagingItems,
  removePackagingItem,
} from 'src/redux/slices/packaging-item';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IPackagingItemListItem,
  IPackagingItemTableFilters,
  IPackagingItemTableFilterValue,
  IPackagingItemTableSort,
} from 'src/types/packaging-item';

import PackagingItemTableRow from '../packaging-item-table-row';
import PackagingItemTableToolbar from '../packaging-item-table-toolbar';

const TABLE_HEAD = [
  { id: 'itemName', label: 'Item', align: 'left' },
  { id: 'quantity', label: 'Quantity', align: 'left' },
  { id: 'status', label: 'Stock Level', align: 'left' },
  { id: 'updatedAt', label: 'Updated', align: 'left' },
  { id: '' },
];

const defaultFilters: IPackagingItemTableFilters = {
  generalSearch: '',
  minQuantity: null,
  maxQuantity: null,
  alertEnabled: 'all',
  units: [],
};

const defaultSortBy: IPackagingItemTableSort = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function PackagingItemListView() {
  const { status, items, error } = useAppSelector((state) => state.packagingItem);

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const handleFilters = useCallback(
    (name: string, value: IPackagingItemTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.packagingItem.edit(id));
    },
    [router]
  );

  const fetchPaginatedItems = useCallback(() => {
    dispatch(
      getPackagingItems({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (item: IPackagingItemListItem, index: number) => {
      dispatch(removePackagingItem({ itemId: item._id }));

      try {
        await dispatch(deletePackagingItem({ item, index })).unwrap();

        fetchPaginatedItems();
        enqueueSnackbar('Packaging item deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedItems]
  );

  const downloadPackagingItemReport = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `packaging/item/generate-report?page=0&limit=0&generalSearch=${
          filters.generalSearch
        }&itemCode=&itemName=&minQuantity=${filters.minQuantity ?? ''}&maxQuantity=${
          filters.maxQuantity ?? ''
        }&unit=${filters.units.join(',')}&alertEnabled=${
          filters.alertEnabled === 'all' ? '' : filters.alertEnabled === 'enabled'
        }&status=&sortBy=itemName&sort=asc`,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/pdf',
          },
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'packaging-item-stock-report.pdf';
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      console.error('Error downloading the PDF:', e);
    }
  }, [filters]);

  useEffect(() => {
    fetchPaginatedItems();
  }, [fetchPaginatedItems]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Packaging Items"
        links={[]}
        action={
          <Stack direction="row" gap={2}>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.DOWNLOAD_INVENTORY_REPORTS,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Tooltip title="Download Inventory Report">
                <Button variant="outlined" color="primary" onClick={downloadPackagingItemReport}>
                  <Iconify icon="eva:arrow-circle-down-fill" width={24} />
                </Button>
              </Tooltip>
            )}

            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_PACKAGING_ITEM,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.packagingItem.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Packaging Item
              </Button>
            )}
          </Stack>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <PackagingItemTableToolbar filters={filters} onFilters={handleFilters} />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={items.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {items?.items.map((row: IPackagingItemListItem, index: number) => (
                      <PackagingItemTableRow
                        key={row._id}
                        row={row}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && items.count === 0 && (
                  <TableNoData message="No packaging items found" notFound={items.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={items.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
